import { EnhancedDataTable } from '@/components/tables/enhanced-data-table'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useDeleteCategory } from '@/utils/api/membership-service'
import type { MembershipCategory } from '@/utils/membership/types'
import { useMembershipCategoryConfigurations } from '@/utils/membership'
import { AlertCircle, Plus } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { CategoryDialog } from './components/CategoryDialog'
import { createColumns } from './components/columns'

export function CategoriesPage() {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<MembershipCategory | undefined>()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [categoryToDelete, setCategoryToDelete] = useState<MembershipCategory | undefined>()

  const {
    categories,
    isLoading,
    error,
    refetch: refetchAllCategories,
  } = useMembershipCategoryConfigurations()

  const deleteMutation = useDeleteCategory({
    mutation: {
      onSuccess: async () => {
        refetchAllCategories()
        toast.success('Category deleted successfully')
      },
      onError: (error: Error) => {
        toast.error(`Failed to delete category: ${error.message}`)
      },
    },
  })

  const handleEdit = (category: MembershipCategory) => {
    setEditingCategory(category)
    setDialogOpen(true)
  }

  const handleDelete = (category: MembershipCategory) => {
    setCategoryToDelete(category)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (categoryToDelete?.id) {
      try {
        console.log('Attempting to delete category:', categoryToDelete.id)
        await deleteMutation.mutateAsync({ id: categoryToDelete.id })
        setDeleteDialogOpen(false)
        setCategoryToDelete(undefined)
        console.log('Category deleted successfully')
      } catch (error) {
        console.error('Delete failed:', error)
        toast.error(`Failed to delete category: ${error instanceof Error ? error.message : 'Unknown error'}`)
      }
    }
  }

  const handleCreateNew = () => {
    setEditingCategory(undefined)
    setDialogOpen(true)
  }

  const handleDialogClose = (open: boolean) => {
    setDialogOpen(open)
    if (!open) {
      setEditingCategory(undefined)
    }
  }

  const columns = createColumns(handleEdit, handleDelete)

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>
          Failed to load categories: {error.message}
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Membership Categories</h1>
          <p className="text-muted-foreground">
            Manage membership categories for registration
          </p>
        </div>
        <Button onClick={handleCreateNew}>
          <Plus className="mr-2 h-4 w-4" />
          New Category
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Categories</CardTitle>
          <CardDescription>
            View and manage all membership categories
          </CardDescription>
        </CardHeader>
        <CardContent>
          <EnhancedDataTable
            data={categories || []}
            columns={columns}
            searchPlaceholder="Search categories..."
            enableGlobalFilter
            enableColumnFilters
            enableExport
            enableColumnVisibility
            enablePagination
            enableSorting
            pageSize={10}
            pageSizeOptions={[5, 10, 20, 30, 50]}
            onRowClick={handleEdit}
            loading={isLoading}
            emptyStateMessage="No categories found. Create one to get started."
            exportOptions={{
              filename: 'membership-categories',
              title: 'Membership Categories Export',
            }}
            initialColumnVisibility={{
              description: false,
              targetAudience: false,
            }}
          />
        </CardContent>
      </Card>

      <CategoryDialog
        open={dialogOpen}
        onOpenChange={handleDialogClose}
        category={editingCategory}
        refetchAllCategories={refetchAllCategories}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the category "{categoryToDelete?.categoryName}".
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
